<template>
  <div :class="wrapperClasses">
    <label :for="checkboxId" class="ui-checkbox__label" @click="handleLabelClick">
      <input
        :id="checkboxId"
        type="checkbox"
        :checked="modelValue"
        :disabled="disabled"
        :required="required"
        :class="checkboxClasses"
        @change="handleChange"
        @blur="handleBlur"
        @focus="handleFocus"
      />
      <span class="ui-checkbox__checkmark">
        <Icon v-if="modelValue" icon="mdi:check" class="ui-checkbox__icon" />
      </span>
      <span v-if="label" class="ui-checkbox__text">{{ label }}</span>
    </label>
    <div v-if="error || helperText" class="ui-checkbox__helper">
      <span v-if="error" class="ui-checkbox__error">{{ error }}</span>
      <span v-else-if="helperText" class="ui-checkbox__helper-text">{{ helperText }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { Icon } from "@iconify/vue";
import type { Size } from "./types";

interface Props {
  modelValue?: boolean;
  label?: string;
  size?: Size;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  id?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  disabled: false,
  required: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
  change: [value: boolean];
}>();

const isFocused = ref(false);
const checkboxId = computed(
  () => props.id || `checkbox-${Math.random().toString(36).substr(2, 9)}`
);

const wrapperClasses = computed(() => {
  const classes = ["ui-checkbox"];
  classes.push(`ui-checkbox--${props.size}`);
  if (props.disabled) classes.push("ui-checkbox--disabled");
  if (props.error) classes.push("ui-checkbox--error");
  return classes;
});

const checkboxClasses = computed(() => {
  const classes = ["ui-checkbox__input"];
  if (isFocused.value) classes.push("ui-checkbox__input--focused");
  return classes;
});

const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.checked;
  emit("update:modelValue", value);
  emit("change", value);
};

const handleBlur = (event: FocusEvent) => {
  isFocused.value = false;
  emit("blur", event);
};

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;
  emit("focus", event);
};

const handleLabelClick = (event: MouseEvent) => {
  if (props.disabled) {
    event.preventDefault();
  }
};
</script>

<style scoped>
.ui-checkbox {
  display: flex;
  flex-direction: column;
  width: fit-content;
}

.ui-checkbox__label {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #344054;
  user-select: none;
}

.ui-checkbox--disabled .ui-checkbox__label {
  cursor: not-allowed;
  opacity: 0.5;
}

.ui-checkbox__input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.ui-checkbox__checkmark {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background-color: var(--color-bg-white);
  border: 2px solid var(--color-border);
  border-radius: calc(var(--radius) - 4px);
  transition: all var(--transition-normal);
  flex-shrink: 0;
}

.ui-checkbox__input:checked + .ui-checkbox__checkmark {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
}

.ui-checkbox__input:focus + .ui-checkbox__checkmark {
  outline: none !important;
  box-shadow: var(--ring) !important;
}

.ui-checkbox__input:hover:not(:disabled) + .ui-checkbox__checkmark {
  border-color: var(--color-text-muted);
}

.ui-checkbox__input:checked:hover:not(:disabled) + .ui-checkbox__checkmark {
  background-color: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
}

.ui-checkbox--disabled .ui-checkbox__checkmark {
  background-color: var(--color-bg-disabled);
  border-color: var(--color-border-light);
  cursor: not-allowed;
}

.ui-checkbox__icon {
  color: var(--color-bg-white);
  font-size: 12px;
  font-weight: bold;
}

.ui-checkbox__text {
  font-size: 12px;
  font-weight: 500;
  color: #344054;
}

.ui-checkbox--disabled .ui-checkbox__text {
  color: var(--color-text-muted);
}

.ui-checkbox__helper {
  margin-top: var(--space-1);
}

.ui-checkbox__error {
  font-size: 12px;
  color: var(--color-danger);
}

.ui-checkbox__helper-text {
  font-size: 12px;
  color: var(--color-text-placeholder);
}

/* Size: sm */
.ui-checkbox--sm .ui-checkbox__checkmark {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border-width: 2px;
}

.ui-checkbox--sm .ui-checkbox__icon {
  font-size: 11px;
}

.ui-checkbox--sm .ui-checkbox__text {
  font-size: 11px;
}

/* Size: lg */
.ui-checkbox--lg .ui-checkbox__checkmark {
  width: 20px;
  height: 20px;
  border-radius: calc(var(--radius) - 2px);
}

.ui-checkbox--lg .ui-checkbox__icon {
  font-size: 14px;
}

.ui-checkbox--lg .ui-checkbox__text {
  font-size: 13px;
}
</style>
